import { useEffect, useMemo, useState } from 'react';
import type { ProductDetailDto, ProductVariantDto } from '@ecom/contracts';
import { useCatalogStore } from '@/stores/catalog.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { count } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { COLOR_OPTION, detailLabel, fabricThumb, fabricsOf } from '@/lib/fabrics';
import { Section, Table, Td, Th, Thumb } from '@/components/page';
import { Alert, Button, Input, cx } from '@/components/ui';
import { gtinOf } from '@ecom/contracts';

/**
 * What ships, and what a courier charges for it.
 *
 * Per variant, because the variant is what goes in the box: a small bag and a
 * large one do not weigh the same. One table, grouped by colour like the
 * variants, edited in place and saved together — thirty-five forms with a
 * save button each was the version this replaces.
 *
 * Typed in kilograms and centimetres, stored in grams and millimetres as
 * integers — a shipping weight is summed and compared, and a float drifts the
 * way a float price does (ADR-004).
 */
export function ShippingCard({ product }: { product: ProductDetailDto }) {
  const { updateVariant, saving, error } = useCatalogStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();

  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [justSaved, setJustSaved] = useState(false);

  // A save answers with the product; what it absorbed is no longer a draft.
  useEffect(() => {
    setDrafts((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([id, draft]) => {
          const variant = product.variants.find((entry) => entry.id === id);
          return variant && JSON.stringify(draft) !== JSON.stringify(toDraft(variant));
        }),
      ),
    );
  }, [product]);

  const fabrics = useMemo(() => fabricsOf(product), [product]);
  const coloured = fabrics.some((fabric) => fabric.color !== null);
  const groups = coloured
    ? [
        ...fabrics.map((fabric) => ({ key: fabric.key, fabric, variants: fabric.variants })),
        ...(product.variants.some((variant) => !variant.options[COLOR_OPTION])
          ? [{ key: '—', fabric: null, variants: product.variants.filter((variant) => !variant.options[COLOR_OPTION]) }]
          : []),
      ]
    : [{ key: 'all', fabric: null, variants: product.variants }];

  const draftOf = (variant: ProductVariantDto) => drafts[variant.id] ?? toDraft(variant);
  const set = (variant: ProductVariantDto, patch: Partial<Draft>) => {
    setJustSaved(false);
    setDrafts((current) => ({ ...current, [variant.id]: { ...draftOf(variant), ...patch } }));
  };
  const changed = Object.keys(drafts);

  async function save() {
    for (const id of changed) {
      const variant = product.variants.find((entry) => entry.id === id);
      const draft = drafts[id];
      if (!variant || !draft) continue;
      /*
       * The API writes the SKU, options, price and default flag on every
       * update, so they go back as they are. The price goes back as "follows
       * the base" when it does — sending the resolved figure pinned it, and a
       * later change of base price then missed this variant. Stock is absent:
       * it moves only through the inventory ledger.
       */
      const ok = await updateVariant(variant.id, {
        sku: variant.sku,
        options: variant.options,
        price: variant.price === product.basePrice ? null : variant.price,
        isDefault: variant.isDefault,
        barcode: draft.barcode.trim() || null,
        mpn: draft.mpn.trim() || null,
        weightGrams: toBase(draft.weightKg, 1000),
        lengthMm: toBase(draft.lengthCm, 10),
        widthMm: toBase(draft.widthCm, 10),
        heightMm: toBase(draft.heightCm, 10),
      });
      if (!ok) return;
    }
    setJustSaved(true);
  }

  const cell = (variant: ProductVariantDto, field: keyof Draft, label: string, inputMode: 'decimal' | 'numeric') => (
    <Input
      dir="ltr"
      inputMode={inputMode}
      aria-label={`${label} — ${variant.sku}`}
      value={draftOf(variant)[field]}
      onChange={(event) => set(variant, { [field]: event.target.value })}
      className={cx('h-9 min-h-9', field === 'barcode' || field === 'mpn' ? 'w-36' : 'w-20')}
    />
  );

  return (
    <Section title={t('tabShipping')} subtitle={t('shippingCardHint')} flush>
      <div className="overflow-x-auto border-t border-line">
        <Table>
          <thead>
            <tr>
              <Th>{t('variant')}</Th>
              <Th>{t('barcode')}</Th>
              <Th>{t('mpn')}</Th>
              <Th>{t('weightKg')}</Th>
              <Th>{t('lengthCm')}</Th>
              <Th>{t('widthCm')}</Th>
              <Th>{t('heightCm')}</Th>
            </tr>
          </thead>
          {groups.map((group) => (
            <tbody key={group.key}>
              {coloured ? (
                <tr className="bg-surface-sunken/70">
                  <td colSpan={7} className="border-b border-line px-5 py-2">
                    <span className="flex items-center gap-2.5">
                      {group.fabric ? (
                        <Thumb src={mediaUrl(fabricThumb(product, group.fabric))} swatch={group.fabric.swatchHex} size="xs" className="rounded-full" />
                      ) : null}
                      <span className="text-sm font-semibold text-ink">{group.fabric?.label ?? t('noColour')}</span>
                      <span className="text-xs text-ink-muted">{count(group.variants.length, locale)}</span>
                    </span>
                  </td>
                </tr>
              ) : null}
              {group.variants.map((variant) => (
                <tr key={variant.id} className={cx(drafts[variant.id] && 'bg-brand-50/40')}>
                  <Td className={coloured ? 'ps-12' : undefined}>
                    <span className="block text-sm text-ink">{detailLabel(product, variant) || variant.sku}</span>
                    <code dir="ltr" className="text-[11px] text-ink-faint">
                      {variant.sku}
                    </code>
                  </Td>
                  <Td>
                    {cell(variant, 'barcode', t('barcode'), 'numeric')}
                    {/* A GTIN or the store's own code (ADR-087): said, never refused. */}
                    {draftOf(variant).barcode.trim() ? (
                      <span className={cx('mt-1 block text-[11px]', gtinOf(draftOf(variant).barcode) ? 'text-success' : 'text-ink-faint')}>
                        {gtinOf(draftOf(variant).barcode) ? t('gtinValid') : t('gtinNot')}
                      </span>
                    ) : null}
                  </Td>
                  <Td>{cell(variant, 'mpn', t('mpn'), 'decimal')}</Td>
                  <Td>{cell(variant, 'weightKg', t('weightKg'), 'decimal')}</Td>
                  <Td>{cell(variant, 'lengthCm', t('lengthCm'), 'decimal')}</Td>
                  <Td>{cell(variant, 'widthCm', t('widthCm'), 'decimal')}</Td>
                  <Td>{cell(variant, 'heightCm', t('heightCm'), 'decimal')}</Td>
                </tr>
              ))}
            </tbody>
          ))}
        </Table>
      </div>

      {product.variants.some((variant) => variant.weightGrams === null) ? (
        <div className="border-t border-line px-5 py-3">
          <p className="text-xs text-ink-muted">{t('weightMissingNote')}</p>
        </div>
      ) : null}

      {changed.length > 0 || justSaved || error ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line bg-brand-50/40 px-5 py-3">
          <span className="me-auto">
            {error ? <Alert>{error}</Alert> : justSaved && changed.length === 0 ? <span className="text-sm text-success">✓ {t('saved')}</span> : null}
          </span>
          {changed.length > 0 ? (
            <>
              <Button variant="secondary" size="sm" onClick={() => setDrafts({})} disabled={saving}>
                {t('discard')}
              </Button>
              <Button size="sm" onClick={() => void save()} disabled={saving}>
                {saving ? t('saving') : t('saveVariantChanges', { count: count(changed.length, locale) })}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}
    </Section>
  );
}

interface Draft {
  barcode: string;
  mpn: string;
  weightKg: string;
  lengthCm: string;
  widthCm: string;
  heightCm: string;
}

function toDraft(variant: ProductVariantDto): Draft {
  return {
    barcode: variant.barcode ?? '',
    mpn: variant.mpn ?? '',
    weightKg: fromBase(variant.weightGrams, 1000),
    lengthCm: fromBase(variant.lengthMm, 10),
    widthCm: fromBase(variant.widthMm, 10),
    heightCm: fromBase(variant.heightMm, 10),
  };
}

/** Base units to what a person types. Integer maths, no float round-trip. */
function fromBase(value: number | null, factor: number): string {
  if (value === null || value === undefined) return '';

  const whole = Math.floor(value / factor);
  const fraction = value % factor;
  if (fraction === 0) return String(whole);

  const places = String(factor).length - 1;
  return `${whole}.${String(fraction).padStart(places, '0').replace(/0+$/, '')}`;
}

function toBase(input: string, factor: number): number | null {
  const trimmed = input.trim();
  if (trimmed === '' || !/^\d*\.?\d*$/.test(trimmed)) return null;

  const [whole = '0', fraction = ''] = trimmed.split('.');
  const places = String(factor).length - 1;
  const value = Number(`${whole}${fraction.padEnd(places, '0').slice(0, places)}`);

  return Number.isSafeInteger(value) && value >= 0 ? value : null;
}
